import { EditorState } from 'hubspot-prosemirror/pm/state';
import { useMemo } from 'react';
import { useProseMirror } from 'use-prosemirror';

import { getDocNodeFromHtml } from './ProsemirrorUtils';
import { EditorConfig } from './editorCapabilities';
import { markFontSize } from './plugins/FontPlugin';
import { buildDefaultRTEConfig } from './utils/configUtils';
import { Schema } from 'hubspot-prosemirror/pm/model';

export default function useHubSpotRTE(
  initialHtml?: string,
  config?: EditorConfig,
  options?: {
    preserveNewLinesInInitialHtml?: boolean;
    preserveWhitespace?: boolean | 'full';
  },
  schema?: Schema
): ReturnType<typeof useProseMirror> {
  const docNode = initialHtml
    ? getDocNodeFromHtml(
        initialHtml,
        {
          ...(options?.preserveNewLinesInInitialHtml !== undefined && {
            preserveNewlines: options.preserveNewLinesInInitialHtml,
          }),
          ...(options?.preserveWhitespace !== undefined && {
            preserveWhitespace: options.preserveWhitespace,
          }),
        },
        schema
      )
    : undefined;

  const [editorState, setEditorState] = useProseMirror({
    doc: docNode,
    ...buildDefaultRTEConfig(undefined, config, schema),
  });

  const state = useFontSize(config, editorState);

  return [state, setEditorState];
}

function useFontSize(
  config: EditorConfig | undefined,
  editorState: EditorState
) {
  return useMemo(() => {
    if (!config || !config.GLOBAL_VARS) return editorState;

    const tr = editorState.tr;

    markFontSize(editorState, tr, config.GLOBAL_VARS);

    return tr.docChanged ? editorState.apply(tr) : editorState;
  }, [config, editorState]);
}
